  import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { ApplicantService } from '@core/services/auth-user/applicant.service';
import { BannerService } from '@core/services/banner.service';
import { PortalDeterminationService } from '@core/services/portal-determination.service';
import { SpinnerService } from '@core/services/spinner.service';
import { SSOService } from '@core/services/sso.service';
import { AuthBehaviors } from '@core/services/token/token-behaviors';
import { TokenService } from '@core/services/token/token.service';
import { ClientSettingsService } from '@features/client-settings/client-settings.service';
import { ClientUserService } from '@features/client-user/client-user.service';
import { SupportService } from '@features/support/support.service';
import { Tab } from '@yourcause/common';
import { I18nService } from '@yourcause/common/i18n';
import { TabsetComponent } from 'ngx-bootstrap/tabs';
import { Observable } from 'rxjs';

@Component({
  selector: 'gc-login-tabs',
  templateUrl: './login-tabs-wrapper.component.html',
  styleUrls: ['./login-tabs-wrapper.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class LoginTabsWrapperComponent implements OnInit {
  @ViewChild('staticTabs') staticTabs: TabsetComponent;
  langKeys = this.clientSettingsService.langKeys;
  loggedIn$: Observable<boolean>;
  tabs: Tab[] = [{
    link: this.portal.isApply ?
      '/apply/auth/signin' :
      '/management/auth/signin',
    labelKey: 'AUTH:textSignIn',
    label: 'Sign in',
    activeRoutes: ['forgot', 'resend-verification', 'confirm-email', 'verification-code', 'bbid-signin']
  }];
  isBBGMApplicant = false;
  ready = false;
  helpLink = this.getHelpCenterLink();

  constructor (
    private spinnerService: SpinnerService,
    private router: Router,
    private ssoService: SSOService,
    private portal: PortalDeterminationService,
    private tokenService: TokenService,
    private authBehaviors: AuthBehaviors,
    private supportService: SupportService,
    private clientSettingsService: ClientSettingsService,
    private bannerService: BannerService,
    private i18n: I18nService,
    private applicantService: ApplicantService,
    private clientUserService: ClientUserService
  ) { }

  get banners () {
    return this.bannerService.banners;
  }

  get isManager () {
    return this.portal.isManager;
  }

  get isMobileResponsive () {
    return this.portal.isApply;
  }

  get liveChatAvailable () {
    return this.supportService.liveChatAvailable;
  }

  async ngOnInit () {
    // if local storage picks up a language they've already set in GrantsConnect,
    // proceed with that language and skip finding the browser locale
    const shouldSkipBrowserLang = !!this.i18n.language && this.i18n.language !== 'en-US';
    if (!shouldSkipBrowserLang) {
      const browserLocale = navigator.languages && navigator.languages.length > 0 ?
        navigator.languages[0] :
        navigator.language || 'en-US';
      const {
        didSet,
        locale
      } = this.i18n.adaptAndSetBrowswerLocale(browserLocale);
      if (didSet) {
        this.setBrowserLocaleToSaveAfterLogin(locale);
      }
    } else {
      this.setBrowserLocaleToSaveAfterLogin(this.i18n.language);
    }

    this.bannerService.setLoginBanners();
    if (this.tokenService.getIsLoggedIn()) {
      this.router.navigateByUrl(this.authBehaviors.current.postLoginRedirect);

      return;
    }
    this.isBBGMApplicant = this.portal.isBBGMApplicant();
    const hasSubdomain = this.portal.hasSubdomain();
    if (hasSubdomain) {
      this.spinnerService.startSpinner();
      await this.ssoService.handleSubDomainOnLoginPage();
      this.spinnerService.stopSpinner();
    }
    this.ready = true;
  }

  setBrowserLocaleToSaveAfterLogin (locale: string) {
    if (this.portal.isApply) {
      this.applicantService.setBrowserLocaleToSaveAfterLogin(locale);
    } else {
      this.clientUserService.setBrowserLocaleToSaveAfterLogin(locale);
    }
  }

  openLiveChat () {
    return this.supportService.openLiveChat();
  }

  openHelpCenter () {
    return this.supportService.openHelpCenter();
  }

  async openSupportEmailModal () {
    return this.supportService.openSupportEmailModal();
  }

  getHelpCenterLink () {
    return this.supportService.getHelpCenterLink();
  }

  onLangChange (lang: string) {
    if (this.portal.isApply) {
      this.applicantService.setCultureToSaveAfterLogin(lang);
    } else {
      this.clientUserService.setCultureToSaveAfterLogin(lang);
    }
  }
}
